import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFormContext } from 'react-hook-form';
import { usePricingRules } from 'src/hooks/usePricingRules';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { formatScope } from 'pages/Discounts/utilities/formatScope';
import { useMemo } from 'react';
import { formatDate } from 'date-fns';
import { PricingRule, DiscountCodeZ as DiscountCode, ConfigurationTypeEnum } from '@libs/schemas';

export const PricingRuleFormField = () => {
  const form = useFormContext<DiscountCode>();
  const { rules } = usePricingRules({
    active: true,
    ruleType: ConfigurationTypeEnum.DISCOUNT,
  });
  const selectedRule = useMemo(() => {
    return rules.find((rule) => rule._id == form.getValues('ruleId'));
  }, [rules, form]);
  return (
    <FormField
      control={form.control}
      name="ruleId"
      render={({ field }) => (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Discount Rule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormItem>
              <FormLabel>Select Rule</FormLabel>
              <FormControl>
                <Select value={field.value} onValueChange={(value) => field.onChange(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a discount rule" />
                  </SelectTrigger>
                  <SelectContent>
                    {rules.map((rule) => {
                      return (
                        <SelectItem key={rule._id} value={rule._id!}>
                          <div className="flex flex-col">
                            <span className="font-medium">{rule.name}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormDescription>
                Note: A pricing rule of type &quot;DISCOUNT&quot; must exist before it can be linked
                to a discount code.
              </FormDescription>
              <FormMessage />
            </FormItem>

            {selectedRule && (
              <div className="p-4 bg-slate-100 rounded-lg dark:bg-slate-800">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Scope:</span>
                    {selectedRule.scope && (
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {formatScope(selectedRule.scope as PricingRule['scope'])}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Effective Period:</span>
                    {selectedRule.effectiveFrom && selectedRule.effectiveTo && (
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {formatDate(selectedRule.effectiveFrom, 'MMM dd, yyyy')} to{' '}
                        {formatDate(selectedRule.effectiveTo, 'MMM dd, yyyy')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    />
  );
};
